/**
 * Контекст аутентификации.
 *
 * Управляет состоянием авторизации на уровне всего приложения:
 * - хранит данные текущего пользователя
 * - предоставляет методы login/logout/register
 * - при загрузке проверяет валидность сохранённого токена
 */

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User } from '../types';
import { authAPI } from '../api/client';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  logout: () => Promise<void>;
}

interface RegisterData {
  email: string;
  password: string;
  first_name: string;
  last_name: string;
  role: string;
  birth_date?: string;
  diagnosis_date?: string;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * Провайдер контекста аутентификации.
 * Оборачивает всё приложение и предоставляет доступ к auth-состоянию.
 */
export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // При загрузке приложения проверяем, есть ли сохранённый токен
  useEffect(() => {
    let cancelled = false;

    const checkAuth = async () => {
      const token = localStorage.getItem('auth_token');
      if (!token) {
        if (!cancelled) setLoading(false);
        return;
      }

      try {
        const response = await authAPI.getMe();
        if (!cancelled) setUser(response.data.user);
      } catch {
        // Токен недействителен — удаляем
        localStorage.removeItem('auth_token');
        localStorage.removeItem('refresh_token');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    checkAuth();
    return () => { cancelled = true; };
  }, []);

  /** Вход в систему: получаем токен и данные пользователя */
  const login = async (email: string, password: string) => {
    const response = await authAPI.login(email, password);
    const { token, refresh_token, user: userData } = response.data;

    localStorage.setItem('auth_token', token);
    localStorage.setItem('refresh_token', refresh_token);
    setUser(userData);
  };

  /** Регистрация нового пользователя */
  const register = async (data: RegisterData) => {
    await authAPI.register(data);
  };

  /** Выход: отзываем refresh-токен на сервере, очищаем состояние */
  const logout = async () => {
    const refreshToken = localStorage.getItem('refresh_token');
    if (refreshToken) {
      try {
        await authAPI.logout(refreshToken);
      } catch {
        // Игнорируем ошибки при logout — токены всё равно удалим
      }
    }
    localStorage.removeItem('auth_token');
    localStorage.removeItem('refresh_token');
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

/**
 * Хук для доступа к контексту аутентификации.
 * Использование: const { user, login, logout } = useAuth();
 */
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
